import type { AxiosError } from "axios";
import type { PriceDaily } from "epsm-api-types/models";
import { useEffect, useState } from "react";

import { SECTORS, TIME_RANGE_MAP } from "../../../config";
import type {
	SectorsChartType,
	SectorsTimeRange,
	SectorsType,
} from "../../../types";
import { createParams } from "../../helpers/createParams";
import { makeRequest } from "../../helpers/makeRequest";
import { SectorsService } from "../../services/SectorsService";

const getPrices = SectorsService.create().getPrices;
const getRatio = SectorsService.create().getRatio;

interface useFetchSectorsProps {
	stocksPrepared: PriceDaily[];
	timeRange: SectorsTimeRange;
	isOnViewport?: boolean;
	chartType: SectorsChartType;
}

export function useFetchSectors(props: useFetchSectorsProps) {
	const { stocksPrepared, timeRange, isOnViewport, chartType } = props;

	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<AxiosError | null>(null);

	useEffect(() => {
		const controllers: AbortController[] = [];
		const pendingRequests = new Set<Promise<void>>();

		if (!isOnViewport || stocksPrepared.length) return;

		setError(null);
		setIsLoading(true);

		for (const t of Object.keys(SECTORS) as SectorsType[]) {
			const controller = new AbortController();
			controllers.push(controller);
			const params = createParams(t, timeRange, chartType);

			const requestConfig = {
				Ratio: () => ({
					requestFn: getRatio,
					options: {
						params,
						signal: controller.signal,
					},
				}),
				Price: () => ({
					requestFn: getPrices,
					options: {
						params,
						path: TIME_RANGE_MAP[timeRange].requestPath,
						signal: controller.signal,
					},
				}),
			};

			const { requestFn, options } = requestConfig[chartType]();

			const request = makeRequest(requestFn, options, stocksPrepared, setError);

			request.finally(() => {
				pendingRequests.delete(request);

				if (pendingRequests.size === 0) {
					setIsLoading(false);
				}
			});

			pendingRequests.add(request);
		}

		return () => {
			for (const controller of controllers) {
				controller.abort();
			}
		};
	}, [timeRange, isOnViewport, chartType, stocksPrepared]);

	return { isLoading, error };
}
